import { ParameterType } from '@/modules/common/enums/parameter-type.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import type { MaskFormData, MaskFormValue } from '@/modules/document/document-master/types/mask.ts';
import type { Parameter } from '@/modules/document/models/parameter.ts';

type CarryParameter = Pick<Parameter, 'type' | 'isList' | 'title' | 'number'>;

type MaskSource = {
  parameters: readonly CarryParameter[];
  formData: MaskFormData;
};

const CONTENT_PARAMETER_TITLE = 'Содержание';

export function createContentSource(content: string): MaskSource {
  return {
    parameters: [{ type: ParameterType.String, isList: false, title: CONTENT_PARAMETER_TITLE, number: 0 }],
    formData: content.trim() ? { 0: content } : {},
  };
}

export type FindListOption = (parameter: CarryParameter, value: Option) => Promise<Option | null>;

function getParameterKey({ type, isList, title }: CarryParameter) {
  return `${type}|${isList}|${title.trim().toLocaleLowerCase()}`;
}

function isFilled(value: MaskFormValue | undefined): value is MaskFormValue {
  return value !== undefined && value !== null && value !== '';
}

function collectSourceValues(sources: MaskSource[]) {
  const values = new Map<string, MaskFormValue[]>();

  sources.forEach(({ parameters, formData }) => {
    const seen = new Map<string, number>();

    parameters.forEach((parameter) => {
      const key = getParameterKey(parameter);
      const index = seen.get(key) ?? 0;
      const value = formData[parameter.number];
      const list = values.get(key) ?? [];

      seen.set(key, index + 1);

      if (isFilled(value) && list[index] === undefined) {
        list[index] = value;
        values.set(key, list);
      }
    });
  });

  return values;
}

export async function carryOverMaskValues(
  parameters: readonly CarryParameter[],
  initial: MaskFormData,
  sources: MaskSource[],
  findListOption: FindListOption
): Promise<MaskFormData> {
  const formData: MaskFormData = {};
  const sourceValues = collectSourceValues(sources);
  const seen = new Map<string, number>();

  await Promise.all(
    parameters.map(async (parameter) => {
      const key = getParameterKey(parameter);
      const index = seen.get(key) ?? 0;

      seen.set(key, index + 1);

      const own = initial[parameter.number];

      if (isFilled(own)) {
        formData[parameter.number] = own;
        return;
      }

      const value = sourceValues.get(key)?.[index];

      if (!isFilled(value)) {
        return;
      }

      if (!parameter.isList) {
        formData[parameter.number] = value;
        return;
      }

      const option = await findListOption(parameter, value as Option).catch(() => null);

      if (option) {
        formData[parameter.number] = option;
      }
    })
  );

  return formData;
}

export function findSameListOption(options: readonly Option[], value: Option) {
  return options.find(({ code, label }) => code === value.code || label === value.label) ?? null;
}
